import React, { useCallback, useEffect } from 'react';
import { getMessage } from 'util/locale';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'app/hooks';

// action Creators
import { selectExportOption } from 'export-selector';

// requesters
import { requestExportSelectorOptions } from 'export-selector/requesters';

// selectors
import { getActiveProductYear, getActiveScriptType } from 'current/selectors';
import { areExportOptionsPending, getExportSelectorOptions } from 'export-selector/selectors';
import { getScriptsForManageScriptList } from 'scripts/selectors';
import { areScriptsLoadImprovementsEnabled } from 'user/selectors';

// components
import { Icon, Selector, Spinner } from '@tcc/crate';

import { trackAction } from 'util/analytics';

const baseString = 'vra-export-selector';
const classString = classNames({
  [`${baseString}`]: true
});
const fieldviewExport = 'fieldview-export';

const ExportSelector = () => {
  const dispatch = useAppDispatch();
  const scripts = useAppSelector(getScriptsForManageScriptList);
  const options = useAppSelector(getExportSelectorOptions);
  const activeProductYear = useAppSelector(getActiveProductYear);
  const activeScriptType = useAppSelector(getActiveScriptType);
  const isScriptsLoadImproved = useAppSelector(areScriptsLoadImprovementsEnabled);
  const areExportOptionsFetching = useAppSelector(areExportOptionsPending);
  const isVisible = Boolean(scripts.length);

  const selectOption = (name, paramString) => {
    // don't do this if we click on the fake footer
    trackAction(`Clicked on Export options / ${name}`);
    if (paramString !== fieldviewExport) {
      dispatch(selectExportOption(paramString));
    }
  };
  
  const requestOptions = useCallback(() => {
    dispatch(requestExportSelectorOptions());
  }, [dispatch]);

  useEffect(() => {
    if (!isScriptsLoadImproved) {
      requestOptions();
    }
  }, [isScriptsLoadImproved, requestOptions]);
  
  if (!isVisible) {
    return null;
  }

  // add footer to first subscreen just once
  const filteredOptions = options.filter(({ id }) => id !== fieldviewExport);
  filteredOptions.push({
    name: (
      <>
        {areExportOptionsFetching && <Spinner testId={`${baseString}-spinner`} />}
        <div>{getMessage('text__fieldview-export')}</div>
      </>
    ),
    id: fieldviewExport
  });
  const name = getMessage('title-text__bulk-export',
    { year: activeProductYear, scriptType: getMessage(activeScriptType) }
  );
  const toggle = (
    <button
      className={`${baseString}__toggle`}
      onClick={requestOptions}
    >
      {getMessage('button-text__export-options')} <Icon glyph={Icon.set.EXPORT} />
    </button>
  );

  return (
    <section
      className={classString}
      data-test-id={baseString}>
      {/* TODO: Use isScrollable prop once available in Crate. */}
      <Selector
        name={name}
        options={filteredOptions}
        setValue={selectOption}
        toggleComponent={toggle}
      />
    </section>
  );
};

export default ExportSelector;
